import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ref, getDownloadURL } from 'firebase/storage'
import { fetchNotifications, markNotificationRead, deleteNotification } from '../lib/api.js'
import type { AppNotification, Job } from '../lib/api.js'
import { storage } from '../lib/firebase.js'
import { usePlayer } from '../hooks/usePlayer.js'
import { cn } from '../lib/cn.js'

interface NotificationDrawerProps {
  open: boolean
  onClose: () => void
}

export default function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const { t } = useTranslation()
  const nav = useNavigate()
  const qc = useQueryClient()
  const player = usePlayer()
  const drawerRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn:  fetchNotifications,
    refetchInterval: open ? 15_000 : false,
  })

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const remove = useMutation({
    mutationFn: deleteNotification,
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  const notifications = data?.notifications ?? []

  const handleClick = async (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id)
    if (n.type === 'job_ready' && n.jobId) {
      // Find job in cached query data
      const cached = qc.getQueryData<{ jobs: Job[] }>(['jobs'])
      const job = cached?.jobs.find(j => j.id === n.jobId)
      if (job) {
        const path = job.outputUrl ?? job.outputStoragePath ?? null
        let audioUrl: string | null = null
        if (path) {
          audioUrl = path.startsWith('http') ? path : await getDownloadURL(ref(storage, path)).catch(() => null)
        }
        if (audioUrl) {
          player.openMiniPlayer(job.id, audioUrl, {
            videoTitle: job.videoTitle ?? t('job.untitled'),
            videoUrl: job.videoUrl ?? '',
            videoThumbnailUrl: job.videoThumbnailUrl ?? '',
            targetLang: job.targetLang,
            sourceLang: job.sourceLang ?? 'auto',
            durationSeconds: job.durationSeconds ?? 0,
            originalStoragePath: job.originalStoragePath ?? null,
          })
          player.openFullScreen()
          onClose()
          return
        }
      }
      // Fallback: navigate to dashboard
      nav('/dashboard')
      onClose()
    } else if (n.jobId) {
      nav('/dashboard')
      onClose()
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-[199] bg-transparent transition-opacity duration-200',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={cn(
          'fixed top-16 right-4 w-[360px] max-h-[calc(100vh-80px)] z-[200]',
          'glass-panel glass-panel-solid rounded-xl flex flex-col overflow-hidden origin-top-right',
          'transition-all duration-220 ease-spring',
          open ? 'translate-y-0 scale-100 opacity-100 pointer-events-auto' : '-translate-y-2 scale-[0.97] opacity-0 pointer-events-none',
        )}
      >
        {/* Header */}
        <div className="p-4 px-5 border-b border-glass-border flex items-center justify-between shrink-0">
          <span className="font-bold text-[0.95rem]">
            {t('notifications.title')}
          </span>
          <button onClick={onClose} className="btn-icon p-1 rounded-md">
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <div className="p-8 text-center text-text-muted text-sm">
              {t('common.loading')}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 px-8 text-center">
              <div className="text-3xl mb-3">🔔</div>
              <p className="text-text-muted text-sm">
                {t('notifications.empty')}
              </p>
            </div>
          ) : (
            notifications.map(n => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={() => handleClick(n)}
                onDelete={() => remove.mutate(n.id)}
              />
            ))
          )}
        </div>
      </div>
    </>
  )
}

function NotificationItem({ notification: n, onClick, onDelete }: { notification: AppNotification; onClick: () => void; onDelete: () => void }) {
  const { t } = useTranslation()
  const isReady  = n.type === 'job_ready'
  const isFailed = n.type === 'job_failed'

  return (
    <div
      onClick={onClick}
      className={cn(
        'group py-3.5 px-5 flex gap-3.5 items-start border-b border-glass-border transition-colors duration-150',
        n.jobId && 'cursor-pointer hover:bg-surface-raised',
        !n.read && 'bg-primary/[0.04]',
      )}
    >
      {/* Icon */}
      <div className={cn(
        'w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-base',
        isReady  && 'bg-success/15 text-success',
        isFailed && 'bg-error/15 text-error',
        !isReady && !isFailed && 'bg-accent/15 text-accent',
      )}>
        {isReady ? '✓' : isFailed ? '✕' : '●'}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className={cn('text-sm mb-0.5 text-text', n.read ? 'font-medium' : 'font-bold')}>
          {n.title}
        </div>
        <div className="text-[0.8rem] text-text-muted leading-snug line-clamp-2">
          {n.body}
        </div>
        <div className="text-xs text-text-muted mt-1">
          {formatRelativeTime(n.createdAt, t)}
        </div>
      </div>

      {/* Delete button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        className="opacity-0 group-hover:opacity-100 shrink-0 mt-0.5 p-1 rounded-md transition-opacity duration-150 hover:bg-error/10 text-text-muted hover:text-error"
        aria-label={t('notifications.delete', 'Delete')}
      >
        <TrashIcon />
      </button>

      {/* Unread dot */}
      {!n.read && (
        <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1" />
      )}
    </div>
  )
}

function formatRelativeTime(ms: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - ms
  const secs  = Math.floor(diff / 1000)
  if (secs < 60)   return t('notifications.justNow')
  const mins  = Math.floor(secs / 60)
  if (mins < 60)   return t('notifications.minutesAgo', { count: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24)  return t('notifications.hoursAgo', { count: hours })
  const days  = Math.floor(hours / 24)
  return t('notifications.daysAgo', { count: days })
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  )
}
